import {
  BookingContext,
  BookingIntent,
  BookingPosition,
  BookRemoteResult,
  IncomingTransfer,
  RemoteResult,
  StockSourceType,
  StockTargetType,
} from '../../types';
import { Adapter } from './adapter';
import {
  StockMovementModel,
  StockMovementType,
  StockObjectType,
  StockObjectTypeCar,
} from 'src/packages/warehouse';
import {
  STOCK_MOVEMENT_TYPE_CAR_RELOCATION,
  STOCK_MOVEMENT_TYPE_CAR_TRANSFER,
  STOCK_MOVEMENT_TYPE_CONSUMPTION,
  STOCK_MOVEMENT_TYPE_MACHINE_TRASH,
  STOCK_MOVEMENT_TYPE_NORMAL,
  STOCK_MOVEMENT_TYPE_ORDER,
  STOCK_MOVEMENT_TYPE_TRASH,
  STOCK_OBJECT_TYPE_BUFFERSTOCK,
  STOCK_OBJECT_TYPE_CAR,
  STOCK_OBJECT_TYPE_STOCK,
  STOCK_OBJECT_TYPE_STOCK_OBJECT,
} from '../external-types';
import { firstValueFrom } from 'rxjs';
import { Log } from '../../util/log';
import {
  HTTP_STATUS_CONFLICT,
  isRetryableRemoteError,
  statusOf,
} from '../../util/remote-error';

const INVALID_ID = -1;

/**
 * Baut StockMovements aus dem Buchungskontext und persistiert sie am Backend
 * (ein-/zweiphasig, Offline-Queue) bzw. lädt eingehende Movements für die
 * Empfangsbestätigung.
 */
export class MovementAdapter extends Adapter {
  /**
   * Baut ein StockMovement aus Position und Buchungskontext (kein I/O). Wählt
   * den Movement-Typ aus Intent bzw. KFZ-Beteiligung und stempelt die
   * `client_id` als Idempotency-Key, damit ein erneuter Versuch dasselbe
   * Movement referenziert.
   *
   * @param position Zu buchende Position
   * @param booking Buchungskontext (Quelle/Ziel/Intent)
   * @param clientId Stabile client_id der logischen Buchung
   * @returns Das unpersistierte Movement.
   */
  public build(
    position: BookingPosition,
    booking: BookingContext,
    clientId: string,
  ): StockMovementModel {
    if (booking.intent === BookingIntent.BUFFERSTOCK_DISPOSAL) {
      return this.buildDisposal(position, booking, clientId);
    }
    return {
      amount: position.amount,
      article_id: position.article_id,
      source_id: booking.sourceId,
      target_id: booking.targetId ?? INVALID_ID,
      source_type: this.mapSourceType(booking.sourceType) ?? undefined,
      target_type: this.mapTargetType(booking.targetType) ?? undefined,
      type: this.movementTypeFor(booking),
      booked_at: new Date(),
      client_id: clientId,
      booking_reference: booking.bookingReference,
      created_by_id: this.adapters.user.getCurrent()?.id ?? INVALID_ID,
      description: booking.description,
    };
  }

  private buildDisposal(
    position: BookingPosition,
    booking: BookingContext,
    clientId: string,
  ): StockMovementModel {
    return {
      amount: position.amount,
      article_id: position.article_id,
      source_id: booking.targetId ?? INVALID_ID, // Bewusst invertiert wegen Backend Konvention
      target_id: booking.sourceId,
      source_type: STOCK_OBJECT_TYPE_BUFFERSTOCK,
      target_type: STOCK_OBJECT_TYPE_STOCK,
      type: STOCK_MOVEMENT_TYPE_MACHINE_TRASH,
      booked_at: new Date(),
      client_id: clientId,
      booking_reference: booking.bookingReference,
      created_by_id: this.adapters.user.getCurrent()?.id ?? INVALID_ID,
      description: booking.description,
    };
  }

  /**
   * Speichert das Movement direkt am Backend und liefert die Server-Response
   * inkl. echter ID zurück, nötig für `bookRemote` (Phase 2). Online-only:
   * ohne Verbindung schlägt der Call fehl (kein Queueing).
   *
   * @param movement Zu speicherndes Movement
   * @returns Flaches Result: `value` null bei Fehler, `retryable` zeigt an,
   *   ob der Fehler transient war (Netz/Lock) und Wiederholung sinnvoll ist.
   */
  public async createRemote(
    movement: StockMovementModel,
  ): Promise<RemoteResult<StockMovementModel>> {
    try {
      const saved = await firstValueFrom(
        this.services.stockMovementService.saveRemote(movement),
      );
      return { value: saved ?? null, retryable: false };
    } catch (error) {
      Log.error('MovementAdapter.createRemote', error, this.logData(movement));
      return { value: null, retryable: isRetryableRemoteError(error) };
    }
  }

  /**
   * Committet Phase 2 einer NormalMovement-Buchung (TransferPoint → Target).
   * Pflicht nach `createRemote` bei Bufferstock-/StockObject-Source, sonst
   * bleibt die Ware unsichtbar im TransferPoint. HTTP 409 wird als
   * `alreadyBooked` gemeldet (bereits bestätigtes Movement).
   *
   * @param movementId ID des in Phase 1 gespeicherten Movements
   * @returns Flaches Result (booked/alreadyBooked/retryable).
   */
  public async bookRemote(movementId: number): Promise<BookRemoteResult> {
    try {
      const currentUserId = this.adapters.user.getCurrent()?.id;
      if (!currentUserId) {
        return { booked: false, alreadyBooked: false, retryable: false };
      }
      await firstValueFrom(
        this.services.stockMovementService.bookMovement(
          movementId,
          new Date(),
          currentUserId ?? INVALID_ID,
        ),
      );
      return { booked: true, alreadyBooked: false, retryable: false };
    } catch (error) {
      if (statusOf(error) === HTTP_STATUS_CONFLICT) {
        return { booked: false, alreadyBooked: true, retryable: false };
      }
      Log.error('MovementAdapter.bookRemote', error, { movementId });
      return {
        booked: false,
        alreadyBooked: false,
        retryable: isRetryableRemoteError(error),
      };
    }
  }

  /**
   * Lädt alle eingehenden, noch unbestätigten Movements mit einem Bufferstock
   * als Ziel (Empfangsliste für die Phase-2-Bestätigung; Backend filtert auf
   * `verified_by_id = nil`). Online-only.
   *
   * Gefiltert wird auf `Warehouse::StockObject`, NICHT `Warehouse::Bufferstock`:
   * das eingehende Movement ist das vom Backend beim Split erzeugte
   * Phase-2-Movement (TransferPoint -> Ziel), dessen `target_type` über die
   * polymorphe Association die STI-base_class trägt (siehe Konstante).
   *
   * @param bufferstockId ID des empfangenden Bufferstocks
   * @returns Eingehende Movements, leer bei Remote-Fehler (z.B. offline).
   */
  public async findIncomingBufferstockTransfers(
    bufferstockId: number,
  ): Promise<IncomingTransfer[]> {
    return this.findIncomingByTarget(
      bufferstockId,
      STOCK_OBJECT_TYPE_STOCK_OBJECT,
    );
  }

  /**
   * Lädt alle eingehenden, noch unbestätigten Movements mit einem Fahrzeug als
   * Ziel (KFZ-Empfangsliste der Umlagerung). Der `target_type`-Filter trennt
   * sie von Bufferstock-Movements mit derselben numerischen ID. Online-only.
   *
   * @param carId ID des empfangenden Fahrzeugs
   * @returns Eingehende Movements, leer bei Remote-Fehler (z.B. offline).
   */
  public async findIncomingCarTransfers(
    carId: number,
  ): Promise<IncomingTransfer[]> {
    return this.findIncomingByTarget(carId, STOCK_OBJECT_TYPE_CAR);
  }

  /**
   * Gemeinsamer Lade-Pfad beider Empfangslisten: filtert serverseitig auf
   * Ziel-ID UND -Typ (disambiguiert gleiche IDs über die Welten hinweg) sowie
   * `verified_by_id = nil` (Backend).
   *
   * @param targetId ID des empfangenden Ziels
   * @param targetType `target_type` des eingehenden Movements, wie das Backend
   *   ihn ablegt (Bufferstock -> `Warehouse::StockObject`, Car ->
   *   `CarManagement::Car`); siehe die aufrufenden Methoden.
   * @returns Eingehende Movements, leer bei Remote-Fehler (z.B. offline).
   */
  private async findIncomingByTarget(
    targetId: number,
    targetType: string,
  ): Promise<IncomingTransfer[]> {
    try {
      const res = await firstValueFrom(
        this.services.stockMovementService.whereRemote({
          target_id: targetId,
          target_type: targetType,
        }),
      );
      // Einzige Stelle, die den Branded Type erzeugt (server-geliefert).
      // Als gelöscht markierte Movements gehören nicht in die Empfangsliste.
      const incoming = (res?.data ?? []).filter(m => !m._destroy);
      return incoming as IncomingTransfer[];
    } catch (error) {
      Log.error('MovementAdapter.findIncomingByTarget', error, {
        targetId,
        targetType,
      });
      return [];
    }
  }

  /**
   * Löscht die lokale Kopie eines Movements (IndexedDB) nach der
   * Empfangsbestätigung. Fehler sind toleriert: der Down-Sync räumt später auf.
   *
   * @param movementId ID des lokal zu löschenden Movements
   * @returns `false` bei Fehler (toleriert, nur geloggt).
   */
  public async removeLocal(movementId: number): Promise<boolean> {
    try {
      await this.services.stockMovementService.localDelete(movementId);
      return true;
    } catch (error) {
      Log.error('MovementAdapter.removeLocal', error, { movementId });
      return false;
    }
  }

  /**
   * Baut die Differenz-Rückbuchung einer Teil-Annahme: NormalMovement vom
   * annehmenden Bufferstock zurück zur ursprünglichen Quelle
   * (`related_source_id`). Selbst-verifiziert (`verified_by_id` gesetzt);
   * Phase 2 committet der Aufrufer per `bookRemote`. Die deterministische
   * `client_id` (`accept-diff:<movementId>`) dedupliziert Wiederholungen.
   *
   * @param accepted Das angenommene eingehende Movement
   * @param amount Differenzmenge (Original − angenommen)
   * @returns Das unpersistierte Rück-Movement.
   */
  public buildTransferReturn(
    accepted: StockMovementModel,
    amount: number,
  ): StockMovementModel {
    const currentUserId = this.adapters.user.getCurrent()?.id ?? INVALID_ID;
    return {
      article_id: accepted.article_id,
      amount,
      source_id: accepted.target_id,
      source_type: STOCK_OBJECT_TYPE_BUFFERSTOCK,
      // Aufrufer (TransferAcceptance) garantiert related_source_id;
      // Sentinel nur für die Typ-Vollständigkeit.
      target_id: accepted.related_source_id ?? INVALID_ID,
      target_type: STOCK_OBJECT_TYPE_BUFFERSTOCK,
      type: STOCK_MOVEMENT_TYPE_NORMAL,
      booked_at: new Date(),
      client_id: `accept-diff:${accepted.id}`,
      // Erbt die Referenz der angenommenen Bewegung, damit der Round-Trip
      // zusammen gruppiert.
      booking_reference: accepted.booking_reference,
      created_by_id: currentUserId,
      verified_by_id: currentUserId,
      book_on_save: false,
    };
  }

  /**
   * Baut die Differenz-Rückbuchung einer KFZ-Teil-Annahme: CarRelocationMovement
   * vom annehmenden Ziel-KFZ zurück zum ursprünglichen Quell-KFZ
   * (`related_source_id`). Der Backend-Split zerlegt es in Phase 1 (Ziel-KFZ →
   * TransferPoint) und Phase 2 (TransferPoint → Quell-KFZ); Phase 2 committet
   * der Aufrufer per `bookRemote`. Die deterministische `client_id`
   * (`accept-diff:<movementId>`) dedupliziert Wiederholungen. Ein lokaler
   * Quell-Spiegel entfällt bewusst (der CarAdapter kennt nur das eingeloggte
   * KFZ); die Quell-Seite heilt über den Backend-Pull des Quell-KFZ.
   *
   * @param accepted Das angenommene eingehende Movement
   * @param amount Differenzmenge (Original − angenommen)
   * @returns Das unpersistierte Rück-Movement.
   */
  public buildCarRelocationReturn(
    accepted: StockMovementModel,
    amount: number,
  ): StockMovementModel {
    const currentUserId = this.adapters.user.getCurrent()?.id ?? INVALID_ID;
    return {
      article_id: accepted.article_id,
      amount,
      source_id: accepted.target_id,
      source_type: STOCK_OBJECT_TYPE_CAR,
      // Aufrufer (TransferAcceptance) garantiert related_source_id;
      // Sentinel nur für die Typ-Vollständigkeit.
      target_id: accepted.related_source_id ?? INVALID_ID,
      target_type: STOCK_OBJECT_TYPE_CAR,
      type: STOCK_MOVEMENT_TYPE_CAR_RELOCATION,
      booked_at: new Date(),
      client_id: `accept-diff:${accepted.id}`,
      // Erbt die Referenz der angenommenen Bewegung, damit der Round-Trip
      // zusammen gruppiert.
      booking_reference: accepted.booking_reference,
      created_by_id: currentUserId,
      verified_by_id: currentUserId,
      book_on_save: false,
    };
  }

  /**
   * Legt das Movement in die Offline-Queue (statt direkt zu senden); der Drainer
   * schickt es später nach, Dedup beim Re-Drain über die `client_id`. Nur für
   * einphasige Movements ohne `bookRemote` (z.B. CarTransferMovement).
   *
   * @param movement Zu queuendes Movement
   * @returns Flaches Result: `value` = gequeuetes Movement (negative lokale
   *   ID), null bei Queue-Fehler (lokal, daher praktisch nie retryable).
   */
  public async enqueue(
    movement: StockMovementModel
  ): Promise<RemoteResult<StockMovementModel>> {
    try {
      const queued = await firstValueFrom(
        this.services.stockMovementService.save(movement)
      );
      return { value: queued ?? null, retryable: false };
    } catch (error) {
      Log.error('MovementAdapter.enqueue', error, this.logData(movement));
      return { value: null, retryable: isRetryableRemoteError(error) };
    }
  }

  /**
   * Korrelations-Daten eines Movements fürs Error-Logging (nur IDs/Mengen).
   *
   * @param movement Movement, dessen Schlüssel geloggt werden
   * @returns Flaches Objekt mit den relevanten IDs/Mengen.
   */
  private logData(
    movement: StockMovementModel,
  ): Record<string, string | number | undefined> {
    return {
      id: movement.id,
      client_id: movement.client_id,
      source_type: movement.source_type,
      source_id: movement.source_id,
      target_type: movement.target_type,
      target_id: movement.target_id,
      article_id: movement.article_id,
      amount: movement.amount,
    };
  }

  private mapSourceType(
    sourceType: StockSourceType,
  ): StockObjectType | StockObjectTypeCar {
    switch (sourceType) {
      case StockSourceType.BUFFERSTOCK:
        return STOCK_OBJECT_TYPE_BUFFERSTOCK;
      case StockSourceType.MACHINE:
        return STOCK_OBJECT_TYPE_STOCK;
      case StockSourceType.CAR:
        return STOCK_OBJECT_TYPE_CAR;
    }
  }

  private mapTargetType(
    targetType: StockTargetType,
  ): StockObjectType | StockObjectTypeCar | null {
    switch (targetType) {
      case StockTargetType.BUFFERSTOCK:
        return STOCK_OBJECT_TYPE_BUFFERSTOCK;
      case StockTargetType.MACHINE:
        return STOCK_OBJECT_TYPE_STOCK;
      case StockTargetType.CAR:
        return STOCK_OBJECT_TYPE_CAR;
    }
    return null;
  }

  // KFZ-Beteiligung bestimmt den Typ: car->car = CarRelocationMovement
  // (zweiphasig), car<->anderes = CarTransferMovement, sonst nach Intent.
  private movementTypeFor(booking: BookingContext): StockMovementType {
    const sourceIsCar = booking.sourceType === StockSourceType.CAR;
    const targetIsCar = booking.targetType === StockTargetType.CAR;
    if (sourceIsCar && targetIsCar) {
      return STOCK_MOVEMENT_TYPE_CAR_RELOCATION;
    }
    if (sourceIsCar || targetIsCar) {
      return STOCK_MOVEMENT_TYPE_CAR_TRANSFER;
    }
    return this.mapMovementType(booking.intent);
  }

  private mapMovementType(intent: BookingIntent): StockMovementType {
    switch (intent) {
      case BookingIntent.MOVE:
        return STOCK_MOVEMENT_TYPE_NORMAL;
      case BookingIntent.ORDER:
        return STOCK_MOVEMENT_TYPE_ORDER;
      case BookingIntent.TRASH:
        return STOCK_MOVEMENT_TYPE_TRASH;
      case BookingIntent.CONSUMPTION:
        return STOCK_MOVEMENT_TYPE_CONSUMPTION;
      case BookingIntent.BUFFERSTOCK_DISPOSAL:
        return STOCK_MOVEMENT_TYPE_MACHINE_TRASH;
    }
  }
}
